import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,  // 增加超时时间到30秒（报告查询可能需要处理大量数据）
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 添加认证token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    // 🐛 调试：检查响应格式
    if (process.env.NODE_ENV === 'development') {
      const contentType = response.headers['content-type'] || '';
      const isJson = contentType.includes('application/json');
      const dataIsString = typeof response.data === 'string';

      // 如果响应数据是字符串但Content-Type是JSON，尝试解析
      if (dataIsString && isJson) {
        try {
          console.warn('⚠️ [API拦截器] 检测到响应数据是字符串，尝试解析JSON');
          response.data = JSON.parse(response.data);
        } catch (parseError) {
          console.error('❌ [API拦截器] JSON解析失败:', parseError);
        }
      }
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // 处理认证失败：清 token 并跳登录页（修：原为不存在的 /login → /auth/login）
      localStorage.removeItem('token');
      if (!window.location.pathname.startsWith('/auth/')) {
        window.location.href = '/auth/login';
      }
    }
    return Promise.reject(error);
  }
);

// 因子库 API
export const factorAPI = {
  listFactors: (params?: Record<string, unknown>) => api.get('/factors/library', { params }),
  getCategories: (params?: Record<string, unknown>) => api.get('/factors/categories', { params }),
  batchDownload: (params: { market: string; format?: string; status?: string }) => 
    api.get('/factors/batch-download', { params, responseType: 'blob' }),
  getFactor: (id: string) => api.get(`/factors/${id}`), // 获取因子详情（包含版本、脚本等）
  getFactorDetail: (id: string) => api.get(`/factors/${id}`), // 别名，保持兼容性
  createFactor: (data: any) => api.post('/factors', data),
  // 新建复合因子(RS_): 选已有因子+权重/方向 → 注册虚拟复合因子(现算, 不物化)
  createComposite: (data: {
    market: string; display_name: string; members: string[];
    weighting?: 'ic_ir' | 'equal'; directions?: Record<string, 'high' | 'low'>; description?: string;
  }) => api.post('/factors/composite', data),
  // 列出复合因子(按人过滤: 自己建的 + 共享研究库; admin 全部)
  listComposites: (market: string) => api.get('/factors/composites', { params: { market } }),
  updateFactor: (factorId: string, data: any) => api.patch(`/factors/${factorId}`, data),
  createVersion: (factorId: string, data: any) => api.post(`/factors/${factorId}/versions`, data),
  publishVersion: (factorId: string, versionId: string) => api.post(`/factors/${factorId}/versions/${versionId}/publish`),
  // L2层: 计算因子值
  calculateFactor: (factorId: string, data: any) => api.post(`/factors/${factorId}/calculate`, data, { timeout: 30000 }), // 因子计算可能需要更长时间，设置30秒超时
  // L3层: 评估因子
  evaluateFactor: (factorId: string, data: any) => api.post(`/factors/${factorId}/evaluate`, data, { timeout: 30000 }), // 因子评估可能需要更长时间，设置30秒超时
  // L3层: 批量评估因子
  batchEvaluateFactors: (data: { factor_ids: string[]; market?: string; triggered_by?: string }) => api.post(`/factors/batch-evaluate`, data, { timeout: 30000 }), // 异步提交，立即返回 job_id
  getBatchEvaluateStatus: (jobId: string) => api.get(`/factors/batch-evaluate/${jobId}`, { timeout: 10000 }), // 轮询任务状态
  // 废弃的API（保持向后兼容）
  runFactor: (factorId: string, data: any) => api.post(`/factors/${factorId}/run`, data, { timeout: 30000 }), // 已废弃，请使用calculateFactor或evaluateFactor
  batchRunFactors: (data: { factor_ids: string[]; market?: string; triggered_by?: string }) => api.post(`/factors/batch-run`, data, { timeout: 300000 }), // 已废弃，请使用batchEvaluateFactors
  listRuns: (factorId: string, params?: Record<string, unknown>) => api.get(`/factors/${factorId}/runs`, { params }),
  getRunDetail: (runId: string) => api.get(`/factors/runs/${runId}`),
  getRunFullDetail: (runId: string) => api.get(`/factors/runs/${runId}/detail`),
  getRunLogs: (runId: string) => api.get(`/factors/runs/${runId}/logs`),
  getFactorScript: (factorId: string, params?: Record<string, unknown>) =>
    api.get(`/factors/${factorId}/script`, { params }),
  upsertFactorScript: (factorId: string, data: any) => api.put(`/factors/${factorId}/script`, data),
  // 读取因子脚本文件内容（从文件系统）
  readFactorScriptContent: (scriptPath: string) =>
    api.post('/factors/scripts/read-content', { script_path: scriptPath }),
  updateFactorStatus: (factorId: string, data: any) => api.patch(`/factors/${factorId}/status`, data),
  deleteFactor: (factorId: string, params?: Record<string, unknown>) =>
    api.delete(`/factors/${factorId}`, { params }),
  getDependents: (factorId: string) => api.get(`/factors/${factorId}/dependents`),
  deleteRun: (runId: string) => api.delete(`/factors/runs/${runId}`),
  registerFactor: (factorId: string, data?: { version_id?: string; triggered_by?: string }) =>
    api.post(`/factors/${factorId}/register`, data || {}, { timeout: 60000 }), // 注册可能需要较长时间
};


// 因子开发 API
export const factorDevelopmentAPI = {
  runCode: (data: { code: string; parameters: any; market: string }) =>
    api.post('/factors/develop/run', data),
  getLogs: (taskId: string) => api.get(`/factors/develop/logs/${taskId}`),
};

// 回测 API
export const backtestAPI = {
  runBacktest: (params: any) => api.post('/backtest/run', params, { timeout: 300000 }), // 回测可能需要较长时间，设置5分钟超时
  getBacktestResults: (id: string) => api.get(`/backtest/${id}`),
  getBacktestPerformance: (id: string) => api.get(`/backtest/${id}/performance`, { timeout: 300000 }), // 获取性能数据可能需要处理大量数据，设置5分钟超时
  checkDataAvailability: (data: { market: string; start_date: string; end_date: string; grouping_config?: any }) =>
    api.post('/backtest/check-data-availability', data),
  // 🆕 报告导出
  exportReport: (runId: string, options?: { format?: string; include_charts?: boolean; include_raw_data?: boolean; include_summary?: boolean }) =>
    api.post(`/backtest/${runId}/export`, options || {}, { responseType: 'blob', timeout: 300000 }),
  // 🆕 历史记录
  getHistory: async (params?: { market?: string; factor_code?: string; status?: string; strategy_definition_id?: string; backtest_type?: string; limit?: number; offset?: number; sort_by?: string; sort_order?: string }) => {
    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const retryDelays = [500, 1200, 2500];
    let lastError: unknown;
    for (let i = 0; i < retryDelays.length; i += 1) {
      try {
        return await api.get('/backtest/history', { params });
      } catch (error) {
        lastError = error;
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        if (status === 503 && i < retryDelays.length - 1) {
          await wait(retryDelays[i]);
          continue;
        }
        throw error;
      }
    }
    throw lastError;
  },
  updateRun: (runId: string, data: { display_name?: string; is_favorite?: boolean }) =>
    api.patch(`/backtest/${runId}`, data),
  deleteRun: (runId: string) => api.delete(`/backtest/${runId}`),
};

// 工作流 API
export const workflowAPI = {
  // 节点类型
  listNodeTypes: (strategyId?: string) => {
    const params = strategyId ? { strategy_id: strategyId } : {};
    return api.get('/workflow/node-types', { params });
  },
  getNodeTypeMetadata: (nodeType: string) => api.get(`/workflow/node-types/${nodeType}`),
  getNodeTypeSchema: (nodeType: string, strategyId?: string) => {
    const params = strategyId ? { strategy_id: strategyId } : {};
    return api.get(`/workflow/node-types/${nodeType}/schema`, { params });
  },
  
  // 节点执行
  executeNode: (data: any) => api.post('/workflow/execute-node', data),
  
  // DSL验证
  validateDSL: (data: { workflow_dsl: any }) => api.post('/workflow/validate-dsl', data),
  
  // 参数解析
  resolveNodeParams: (nodeConfig: any, strategyParams: any) => 
    api.post('/workflow/resolve-node-params', {
      node_config: nodeConfig,
      strategy_params: strategyParams
    }),
};

// 策略工作流 API
export const strategyWorkflowAPI = {
  // 测试运行
  testWorkflow: (data: any) => api.post('/strategy-run/test', data),
  getRunDetail: (runId: string) => api.get(`/strategy-run/${runId}`),
  
  // 版本管理
  listVersions: (strategyId: string) => api.get(`/strategies/${strategyId}/versions`),
  rollbackVersion: (strategyId: string, versionId: string) => 
    api.post(`/strategies/${strategyId}/versions/${versionId}/rollback`),
};

// 数据更新 API
export const dataUpdateAPI = {
  // 启动数据更新任务
  startUpdate: (data: {
    strategy_id: string;
    date_mode: string;
    trade_date?: string;
    market: string;
    update_market_data: boolean;
    update_factor_data: boolean;
    update_metadata: boolean;
  }) => api.post('/data-update/start', data),
  
  // 获取更新任务状态
  getUpdateStatus: (jobId: string) => api.get(`/data-update/status/${jobId}`),
};

// 策略测试运行 API（新增）
export const strategyTestRunAPI = {
  // 测试运行
  testRun: (strategyId: string, data: {
    date_mode: 'today' | 'latest_trade_date' | 'specified';
    trade_date?: string;
    market: string;
    parameters: Record<string, any>;
  }) => api.post(`/strategy-runs/strategies/${strategyId}/test-run`, data),
  
  // 查询运行状态（完整）
  getRunStatus: (runId: string) => api.get(`/strategy-runs/${runId}`),
  
  // 查询运行状态（轻量级，用于轮询）
  getRunStatusLight: (runId: string) => api.get(`/strategy-runs/${runId}/status`, { timeout: 2000 }),
  
  // 查询运行日志
  getRunLogs: (runId: string, params?: {
    level?: 'INFO' | 'WARNING' | 'ERROR';
    node_id?: string;
    limit?: number;
    offset?: number;
  }) => api.get(`/strategy-runs/${runId}/logs`, { params }),
  
  // 查询性能指标
  getRunMetrics: (runId: string) => api.get(`/strategy-runs/${runId}/metrics`),
  
  // 查询依赖关系
  getRunDependencies: (runId: string) => api.get(`/strategy-runs/${runId}/dependencies`),
  
  // 下载 CSV
  downloadCSV: (runId: string) => api.get(`/strategy-runs/${runId}/download`, { responseType: 'blob' }),
  
  // 发布到报告中心
  publishRun: (runId: string, data?: {
    snapshot_name?: string;
    generate_csv?: boolean;
    summary?: any;
  }) => api.post(`/strategy-runs/${runId}/publish`, data),
};

// 快照 API
export const snapshotAPI = {
  // 基于快照运行择时引擎
  runTiming: (snapshotId: string, data?: {
    timing_strategy?: string;
    trade_date?: string;
    write_signals?: boolean;
    mode?: string;
  }) => api.post(`/selection-snapshots/${snapshotId}/run-timing`, data),
};

// 策略工作台 API
export const strategyAPI = {
  // 策略 CRUD
  listStrategies: (params?: {
    market?: string;
    domain?: string; // 新增：一级分类筛选
    family?: string; // 新增：二级分类筛选
    method_tags?: string; // 新增：方法标签筛选（逗号分隔）
    apply_scope?: string; // 新增：作用范围筛选
    type?: string; // 兼容保留
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) => api.get('/strategies', { params }),
  getStrategy: (id: string) => api.get(`/strategies/${id}`),
  // 新增：获取分类字典
  getTaxonomy: () => api.get('/strategies/taxonomy'),
  // 新增：获取策略元数据
  getStrategyMeta: (strategyCode: string) => api.get(`/strategies/${strategyCode}/meta`),
  createStrategy: (data: any) => api.post('/strategies', data),
  updateStrategy: (id: string, data: any) => api.put(`/strategies/${id}`, data),
  deleteStrategy: (id: string) => api.delete(`/strategies/${id}`),
  
  // 脚本管理
  listScripts: (strategyId: string, options?: { group?: string, includeAssociations?: boolean }) => {
    const params: Record<string, string> = {};
    if (options?.group) params.group = options.group;
    if (options?.includeAssociations) params.include_associations = 'true';
    return api.get(`/strategies/${strategyId}/scripts`, { params });
  },
  extractScripts: (strategyId: string) => api.get(`/strategies/${strategyId}/scripts/extract`),
  getScriptAssociations: (strategyId: string, scriptPath?: string) => {
    const params = scriptPath ? { script_path: scriptPath } : {};
    return api.get(`/strategies/${strategyId}/scripts/associations`, { params });
  },
  createScript: (strategyId: string, data: any) => api.post(`/strategies/${strategyId}/scripts`, data),
  getScript: (scriptId: string) => api.get(`/strategies/scripts/${scriptId}`),
  readScriptContent: (scriptPath: string) => api.post('/strategies/scripts/read-content', { script_path: scriptPath }),
  updateScript: (scriptId: string, data: any) => api.put(`/strategies/scripts/${scriptId}`, data),
  deleteScript: (scriptId: string) => api.delete(`/strategies/scripts/${scriptId}`),
  
  // 参数管理
  listParameters: (strategyId: string) => api.get(`/strategies/${strategyId}/parameters`),
  createParameters: (strategyId: string, data: any) => api.post(`/strategies/${strategyId}/parameters`, data),
  getParameters: (parameterId: string) => api.get(`/strategies/parameters/${parameterId}`),
  updateParameters: (parameterId: string, data: any) => api.put(`/strategies/parameters/${parameterId}`, data),
  deleteParameters: (parameterId: string) => api.delete(`/strategies/parameters/${parameterId}`),
  
  // 测试运行
  testRun: (strategyId: string, data: any) => api.post(`/strategies/${strategyId}/test-run`, data),
};

// 策略配置 API
export const strategyConfigAPI = {
  // 获取策略前端配置（合并后的完整配置）
  getFrontendConfig: (strategyId: string) => api.get(`/strategy-config/strategies/${strategyId}/frontend-config`),
  
  // 更新策略前端配置（仅更新策略特定部分）
  updateFrontendConfig: (strategyId: string, data: { frontend_config: any }) => 
    api.put(`/strategy-config/strategies/${strategyId}/frontend-config`, data),
  
  // 获取默认配置（仅从文件加载）
  getDefaultConfig: (strategyCode: string) => api.get(`/strategy-config/strategies/${strategyCode}/default-config`),
  
  // 更新默认配置（保存到文件）
  updateDefaultConfig: (strategyCode: string, data: any) => 
    api.put(`/strategy-config/strategies/${strategyCode}/default-config`, data),
};

// 信号 API
export const signalAPI = {
  getSignals: (params?: any) => api.get('/signals', { params }),
  getTasks: () => api.get('/signals/tasks'),
  toggleTask: (id: string, enabled: boolean) =>
    api.put(`/signals/tasks/${id}`, { enabled }),
  exportSignals: (params?: any) => api.get('/signals/export', { params }),
};

// 个股查询 API
export const stockAPI = {
  searchStock: (query: string) => api.get(`/stocks/search?q=${query}`),
  getStockInfo: (symbol: string) => api.get(`/stocks/${symbol}`),
  getStockPrice: (symbol: string, params?: any) =>
    api.get(`/stocks/${symbol}/price`, { params }),
  getStockIndicators: (symbol: string) => api.get(`/stocks/${symbol}/indicators`),
  getStockFactorExposures: (symbol: string) =>
    api.get(`/stocks/${symbol}/factor-exposures`),
};

// 监控 API
export const monitoringAPI = {
  getSystemStatus: () => api.get('/monitoring/system-status'),
  getDataFreshness: (market: string) => api.get(`/monitoring/freshness/${market}`, { timeout: 30000 }), // 增加超时时间到30秒
  getDataDateRange: (market: string, dataType: string = 'daily') => 
    api.get('/monitoring/data-date-range', { params: { market, data_type: dataType } }),
  getTaskFlow: () => api.get('/monitoring/task-flow'),
  getStrategyTasks: (market?: string, strategy?: string, tradeDate?: string) => 
    api.get('/monitoring/strategy-tasks', { 
      params: { 
        market,
        strategy: strategy || 'sector_rotation',
        trade_date: tradeDate 
      } 
    }),
  getStrategyTaskDetail: (taskId: string) => 
    api.get(`/monitoring/strategy-tasks/${taskId}`),
  rerunStrategyTask: (taskId: string) => 
    api.post(`/monitoring/strategy-tasks/${taskId}/rerun`),
  getAlerts: (market?: string, limit?: number) => api.get('/monitoring/alerts', { 
    params: { 
      ...(market ? { market } : {}),
      ...(limit ? { limit } : {})
    } 
  }),
  getMetricsHistory: (params?: any) => api.get('/monitoring/metrics/history', { params }),
  // 数据更新任务相关接口（新增）
  getTaskDetail: (taskId: string) => api.get(`/monitoring/tasks/${taskId}`),
  getTaskList: (params?: any) => api.get('/monitoring/tasks', { params }),
  rerunTask: (taskId: string) => api.post(`/monitoring/tasks/${taskId}/rerun`),
  triggerTask: (taskType: string, market: string, tradeDate?: string) =>
    api.post('/monitoring/tasks/trigger', { task_type: taskType, market, trade_date: tradeDate }),
  // 服务器快照健康（2026-04-23 新增，阶段 3）
  getSnapshotStatus: (days = 7) =>
    api.get('/monitoring/snapshot-status', { params: { days } }),
  // 因子链健康：数据新鲜度 + 连续失败次数
  getFactorChainHealth: (market: string) =>
    api.get('/monitoring/factor-chain-health', { params: { market } }),
};

// 数据任务中心 API（/api/tasks/*）
export const taskAPI = {
  listTasks: (market?: string) =>
    api.get('/tasks', { params: market ? { market } : {} }),
  getHistory: (taskId: string, days = 30, page = 1, perPage = 20) =>
    api.get(`/tasks/${taskId}/history`, { params: { days, page, per_page: perPage } }),
  trigger: (taskId: string, mode?: 'latest' | 'backfill') =>
    api.post(`/tasks/${taskId}/trigger`, mode ? { mode } : {}),
  enable: (taskId: string) =>
    api.post(`/tasks/${taskId}/schedule/enable`),
  disable: (taskId: string) =>
    api.post(`/tasks/${taskId}/schedule/disable`),
  getFreshness: () =>
    api.get('/tasks/freshness'),
  getCoverage: (taskId: string) =>
    api.get(`/tasks/${taskId}/coverage`),
  getDependencyStatus: (market?: string) =>
    api.get('/tasks/dependency-status', { params: market ? { market } : {} }),
  getRecoveryPlan: (market?: string) =>
    api.get('/tasks/recovery-plan', { params: market ? { market } : {} }),
  triggerForce: (taskId: string, mode: 'latest' | 'backfill' = 'latest') =>
    api.post(`/tasks/${taskId}/trigger`, { mode, force: true }),
};

// 报告 API
export const reportAPI = {
  // 旧接口（保持兼容）
  getStrategyReports: (params?: any) => api.get('/reports/strategy', { params }),
  getFactorReports: (params?: any) => api.get('/reports/factor', { params }),
  getSignalReports: (params?: any) => api.get('/reports/signals', { params }),
  
  // 新接口
  getFactorDailyReports: (params?: any) => api.get('/reports/factor-daily', { params }),
  getSelectionResults: (params?: any) => api.get('/reports/selection-results', { params }),
  getTimingSignals: (params?: {
    market?: string;
    strategy_code?: string;
    strategy_type?: string;
    signal_type?: string;
    action?: string;
    target?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
    order_by?: string;
    order_dir?: 'asc' | 'desc';
    page?: number;
    page_size?: number;
  }) => api.get('/reports/timing-signals', { params }),
  
  // 下载接口
  downloadReport: (type: string, format: string = 'csv', params?: any) => {
    return api.get('/reports/download', {
      params: { type, format, ...params },
      responseType: 'blob'
    });
  },
  
  getReport: (id: string) => api.get(`/reports/${id}`),
  
  // 兼容旧接口
  getReports: (params?: any) => {
    const category = params?.category || 'strategy';
    if (category === 'strategy') {
      return api.get('/reports/strategy', { params });
    } else if (category === 'factor') {
      return api.get('/reports/factor', { params });
    } else if (category === 'signals') {
      return api.get('/reports/signals', { params });
    }
    return api.get('/reports/strategy', { params });
  },
};

// SQL 查询 API
export const sqlAPI = {
  execute: (sql: string) => api.post('/sql/execute', { sql }),
  getHistory: () => api.get('/sql/history'),
};

// 聊天 API (智研Bot)
export const chatAPI = {
  // 发送消息
  sendMessage: (message: string, conversationId?: string) =>
    api.post('/chat/message', { message, conversation_id: conversationId }),
  
  // 获取会话历史
  getHistory: (conversationId: string, params?: { limit?: number; offset?: number }) =>
    api.get(`/chat/history`, { params: { conversation_id: conversationId, ...params } }),
  
  // 获取配置
  getConfig: () => api.get('/chat/config'),
  
  // 获取会话列表
  getConversations: (params?: { limit?: number; offset?: number }) =>
    api.get('/chat/conversations', { params }),
  
  // 创建会话
  createConversation: (title?: string) =>
    api.post('/chat/conversations', title ? { title } : {}),
  
  // 删除会话
  deleteConversation: (conversationId: string) =>
    api.delete(`/chat/conversations/${conversationId}`),
};

// 推荐域 API (保留原有接口)
export const llmFactorAPI = {
  uploadPDF: (file: File, market: string = 'cn') => {
    const form = new FormData();
    form.append('file', file);
    form.append('market', market);
    return api.post('/llm-factor/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
  },
  getJob: (jobId: string) => api.get(`/llm-factor/jobs/${jobId}`),
  getCandidates: (status?: string) =>
    api.get('/llm-factor/candidates', { params: status ? { status } : {} }),
  getCandidate: (id: string) => api.get(`/llm-factor/candidates/${id}`),
  promote: (id: string, market: string = 'cn') =>
    api.post(`/llm-factor/candidates/${id}/promote`, { market }),
  reject: (id: string) => api.post(`/llm-factor/candidates/${id}/reject`),
};

// 研报研究 API（Agent B 可视化）
export const analystAPI = {
  bStatus: (date: string, market = 'cn') =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(
      '/agent/b-status', { params: { date, market } }
    ),
  bSignals: (date: string, market = 'cn') =>
    api.get<{ success: boolean; data: unknown[] }>(
      '/agent/b-signals', { params: { date, market } }
    ),
  analystReports: (tsCode: string, date: string) =>
    api.get<{ success: boolean; data: unknown[] }>(
      '/agent/analyst-reports', { params: { ts_code: tsCode, date } }
    ),
  analystReportsRange: (start: string, end: string, market = 'cn') =>
    api.get<{ success: boolean; data: unknown[] }>(
      '/agent/analyst-reports-range', { params: { start, end, market } }
    ),
  closePrices: (codes: string[], date: string, market = 'cn') =>
    api.get<{ success: boolean; data: Record<string, number> }>(
      '/agent/close-prices', { params: { codes: codes.join(','), date, market } }
    ),
  // ── 研报知识库(KB)叙事 + 专家投票 ──
  kbStock: (stockCode: string) =>
    api.get<{ success: boolean; data: { stock_code: string; reports: unknown[]; consensus: Record<string, unknown> } }>(
      `/agent/kb/stock/${encodeURIComponent(stockCode)}`
    ),
  kbSearch: (keyword: string, limit = 20) =>
    api.get<{ success: boolean; data: unknown[] }>(
      '/agent/kb/search', { params: { keyword, limit } }
    ),
  kbStats: () =>
    api.get<{ success: boolean; data: { total: number; done: number; failed: number; pending: number } }>(
      '/agent/kb/stats'
    ),
  kbVotes: (date: string, stock?: string) =>
    api.get<{ success: boolean; data: { date: string; votes: unknown[] } }>(
      '/agent/kb/votes', { params: { date, ...(stock ? { stock } : {}) } }
    ),
  kbConsensus: (date: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(
      '/agent/kb/consensus', { params: { date } }
    ),
};

export const recommendationAPI = {
  healthCheck: () => api.get('/health'),
  getStrategies: () => api.get('/strategies'),
  getStrategyInfo: (strategyId: string) => api.get(`/strategies/${strategyId}`),
  generateRecommendations: (strategyId: string, data?: any) =>
    api.post(`/strategies/${strategyId}/recommendations`, data),
  getRunnerStatus: () => api.get('/runner/status'),
  getRecommendationHistory: (params?: any) => api.get('/history', { params }),
};

// 策略测试运行相关类型（新增）
export interface DataUpdateConfig {
  enabled: boolean;
  update_market_data?: boolean;
  update_factor_data?: boolean;
  update_metadata?: boolean;
}

export interface TestRunRequest {
  date_mode: 'today' | 'latest_trade_date' | 'specified';
  trade_date?: string;
  market: string;
  parameters?: Record<string, any>;
  update_data_config?: DataUpdateConfig;
}

export interface SectorResult {
  sector_code: string;
  sector_name: string;
  score: number;
  rank: number;
  stocks?: Array<{
    symbol: string;
    name: string;
    weight?: number;
  }>;
}

export interface StockResult {
  symbol: string;
  name: string;
  score: number;
  weight?: number;
}

export interface TestRunResponse {
  run_id: string;
  trade_date: string;
  status: 'pending' | 'running' | 'queued' | 'completed' | 'failed';
  result?: {
    sectors?: SectorResult[];
    stocks?: StockResult[];
  };
  data_freshness?: {
    daily_db_max_date: string;
    stale: boolean;
    missing_tables: string[];
  };
  artifacts?: {
    csv_path: string;
  };
  message?: string;
  execution_log?: Array<{
    timestamp: string;
    level: string;
    message: string;
    node_id?: string;
    context?: any;
  }>;
  started_at?: string;
  finished_at?: string;
}

export interface RunStatusLight {
  run_id: string;
  status: 'pending' | 'running' | 'queued' | 'completed' | 'failed';
  progress?: number;
  current_step?: string;
  total_steps?: number;
  started_at?: string;
  last_heartbeat?: string;
  estimated_remaining_time?: number;
}

export interface Factor {
  id: string;
  name: string;
  display_name: string;
  category: string;
  description: string;
  status: 'active' | 'testing' | 'inactive' | 'deprecated';
  last_calculated?: string;
  performance?: {
    ic_mean: number;
    ic_ir: number;
    turnover: number;
  };
}

export interface BacktestResult {
  id: string;
  strategy: string;
  start_date: string;
  end_date: string;
  performance: {
    annual_return: number;
    sharpe_ratio: number;
    max_drawdown: number;
    win_rate: number;
  };
}

export interface Signal {
  id: string;
  symbol: string;
  market: string;
  strategy: string;
  signal: 'BUY' | 'SELL' | 'HOLD';
  confidence: number;
  price: number;
  timestamp: string;
}

export default api;

